import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FreeResourcesBar from '../components/FreeResourcesBar';
import { Button } from '../components/ui/button';
import { 
  Clock, Calendar, Award, CheckCircle, ArrowRight, Book, 
  Code, Briefcase, Target, Users, Star, Download 
} from 'lucide-react';

const ProgramDetailPage = () => {
  const { programId } = useParams();
  const navigate = useNavigate();

  const IT_FOUNDATIONS_PDF = 'https://customer-assets.emergentagent.com/job_techpath-preview/artifacts/49hkm8np_Kick-Start%20Your%20IT%20Career%20%E2%80%93%20The%208-Week%20Foundations%20Program.pdf';

  const programsData = {
    'ai-engineer': {
      title: 'AI Engineer Program',
      subtitle: 'Master Machine Learning, Deep Learning, and AI Development',
      duration: '8 Weeks',
      level: 'Intermediate to Advanced',
      format: 'Hybrid (Online + In-Person Labs)',
      price: 'Contact for Pricing',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
      overview: 'Transform into an AI Engineer with our comprehensive program covering machine learning, deep learning, neural networks, and practical AI development. Learn to build intelligent systems that solve real-world problems.',
      modules: [
        { week: 'Week 1', title: 'Python & Data Science Fundamentals', topics: ['Python Programming', 'NumPy & Pandas', 'Data Visualization', 'Statistics Basics'] },
        { week: 'Week 2', title: 'Machine Learning Foundations', topics: ['Supervised Learning', 'Unsupervised Learning', 'Model Evaluation', 'Scikit-learn'] },
        { week: 'Week 3-4', title: 'Deep Learning & Neural Networks', topics: ['Neural Networks', 'TensorFlow & PyTorch', 'CNNs', 'Transfer Learning'] },
        { week: 'Week 5', title: 'Natural Language Processing', topics: ['Text Processing', 'NLP Models', 'Transformers', 'LLM Basics'] },
        { week: 'Week 6', title: 'Computer Vision', topics: ['Image Processing', 'Object Detection', 'Image Classification', 'CV Applications'] },
        { week: 'Week 7', title: 'MLOps & Deployment', topics: ['Model Deployment', 'Cloud AI Services', 'API Development', 'Model Monitoring'] },
        { week: 'Week 8', title: 'Capstone Project', topics: ['Real-world AI Project', 'End-to-end Development', 'Portfolio Building', 'Presentation'] }
      ],
      skills: ['Python Programming','Machine Learning Algorithms','Deep Learning with TensorFlow','Natural Language Processing','Computer Vision','MLOps & Model Deployment','Data Analysis & Visualization','AI Project Management'],
      tools: ['Python', 'TensorFlow', 'PyTorch', 'Scikit-learn', 'Jupyter', 'Pandas', 'NumPy', 'Git', 'Docker', 'AWS SageMaker'],
      careerPaths: ['AI Engineer','Machine Learning Engineer','Data Scientist','ML Researcher','Computer Vision Engineer','NLP Engineer'],
      salaryRange: '$75,000 - $120,000',
      prerequisites: ['Basic programming knowledge (any language)','Understanding of mathematics (algebra, calculus basics)','Completed IT degree or equivalent experience','Passion for AI and problem-solving']
    },

    'cloud-engineer': {
      title: 'Cloud Engineer Program',
      subtitle: 'Master AWS, Azure, and Google Cloud Platforms',
      duration: '8 Weeks',
      level: 'Beginner to Advanced',
      format: 'Hybrid (Online + In-Person Labs)',
      price: 'Contact for Pricing',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80',
      overview: 'Become a Cloud Engineer and master cloud infrastructure across AWS, Azure, and Google Cloud Platform. Learn to design, deploy, and manage scalable cloud solutions.',
      modules: [
        { week: 'Week 1', title: 'Cloud Fundamentals & AWS Basics', topics: ['Cloud Computing Concepts', 'AWS Core Services', 'EC2 & S3', 'IAM & Security'] },
        { week: 'Week 2', title: 'AWS Advanced Services', topics: ['RDS & DynamoDB', 'Lambda & Serverless', 'VPC & Networking', 'CloudFormation'] },
        { week: 'Week 3', title: 'Microsoft Azure', topics: ['Azure Core Services', 'Virtual Machines', 'Azure Storage', 'Azure Networking'] },
        { week: 'Week 4', title: 'Azure Advanced & DevOps', topics: ['Azure Functions', 'Azure DevOps', 'ARM Templates', 'Azure Monitor'] },
        { week: 'Week 5', title: 'Google Cloud Platform', topics: ['GCP Core Services', 'Compute Engine', 'Cloud Storage', 'GCP Networking'] },
        { week: 'Week 6', title: 'Multi-Cloud & IaC', topics: ['Terraform Basics', 'Multi-cloud Strategies', 'Infrastructure as Code', 'Cloud Migration'] },
        { week: 'Week 7', title: 'Cloud Security & Compliance', topics: ['Security Best Practices', 'Compliance Standards', 'Monitoring & Logging', 'Cost Optimization'] },
        { week: 'Week 8', title: 'Capstone Project', topics: ['Multi-cloud Architecture', 'Real-world Deployment', 'Portfolio Building', 'Certification Prep'] }
      ],
      skills: ['AWS (EC2, S3, Lambda, RDS)','Azure (VMs, Functions, DevOps)','Google Cloud Platform','Infrastructure as Code (Terraform)','Cloud Security & IAM','Networking & VPC','Automation & Scripting','Cost Optimization'],
      tools: ['AWS Console', 'Azure Portal', 'Google Cloud Console', 'Terraform', 'Docker', 'Kubernetes', 'Git', 'Python/Bash', 'CloudFormation', 'ARM Templates'],
      careerPaths: ['Cloud Engineer','Cloud Architect','DevOps Engineer','Cloud Security Engineer','Site Reliability Engineer','Solutions Architect'],
      salaryRange: '$70,000 - $115,000',
      prerequisites: ['Basic IT knowledge','Understanding of networking basics','Linux command line familiarity','Completed IT Foundations or equivalent']
    },

    'devops-engineer': {
      title: 'DevOps Engineer Program',
      subtitle: 'Master CI/CD, Automation, Kubernetes & Cloud Infrastructure',
      duration: '8 Weeks',
      level: 'Intermediate',
      format: 'Hybrid (Online + In-Person Labs)',
      price: 'Contact for Pricing',
      image: 'https://res.cloudinary.com/practicaldev/image/fetch/s--jEUHkXsP--/c_imagga_scale,f_auto,fl_progressive,h_420,q_auto,w_1000/https://dev-to-uploads.s3.amazonaws.com/uploads/articles/3b8tcid7y399f8hzxps8.png',
      overview: 'Become a DevOps Engineer and master the full software delivery lifecycle. Learn automation, CI/CD, containerization, orchestration, and infrastructure as code.',
      modules: [
        { week: 'Week 1', title: 'DevOps Fundamentals & Linux', topics: ['DevOps Culture', 'Linux Administration', 'Shell Scripting', 'Git & Version Control'] },
        { week: 'Week 2', title: 'CI/CD & Automation', topics: ['Jenkins & GitLab CI', 'Build Automation', 'Testing Automation', 'Deployment Pipelines'] },
        { week: 'Week 3', title: 'Containerization with Docker', topics: ['Docker Fundamentals', 'Dockerfile', 'Docker Compose', 'Container Registry'] },
        { week: 'Week 4', title: 'Kubernetes Orchestration', topics: ['Kubernetes Architecture', 'Pods & Deployments', 'Services & Ingress', 'ConfigMaps & Secrets'] },
        { week: 'Week 5', title: 'Infrastructure as Code', topics: ['Terraform Basics', 'Ansible Automation', 'CloudFormation', 'Configuration Management'] },
        { week: 'Week 6', title: 'Cloud DevOps (AWS/Azure)', topics: ['Cloud CI/CD', 'Cloud Automation', 'Cloud Monitoring', 'Cloud Security'] },
        { week: 'Week 7', title: 'Monitoring & Observability', topics: ['Prometheus & Grafana', 'ELK Stack', 'APM Tools', 'Incident Management'] },
        { week: 'Week 8', title: 'DevOps Project', topics: ['End-to-end Pipeline', 'Production Deployment', 'Portfolio Building', 'Best Practices'] }
      ],
      skills: ['CI/CD Pipelines','Docker & Kubernetes','Infrastructure as Code (Terraform)','Cloud Platforms (AWS/Azure)','Automation Scripting','Monitoring & Logging','Version Control (Git)','Linux Administration'],
      tools: ['Jenkins', 'GitLab', 'Docker', 'Kubernetes', 'Terraform', 'Ansible', 'Prometheus', 'Grafana', 'Git', 'Linux'],
      careerPaths: ['DevOps Engineer','Site Reliability Engineer (SRE)','Release Engineer','Infrastructure Engineer','Automation Engineer','Platform Engineer'],
      salaryRange: '$75,000 - $115,000',
      prerequisites: ['Basic programming knowledge','Understanding of software development','Linux command line basics','Completed IT degree or equivalent']
    },

    'cybersecurity-specialist': {
      title: 'Cybersecurity Specialist Program',
      subtitle: 'Master Security Fundamentals, Threat Detection & Incident Response',
      duration: '8 Weeks',
      level: 'Intermediate',
      format: '100% Online • Live Mentor Support',
      price: 'Contact for Pricing',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80',
      overview: 'Learn to protect organizations from cyber threats. Master security fundamentals, threat detection, vulnerability assessment, incident response, and security tools to become a job-ready cybersecurity specialist.',
      modules: [
        { week: 'Week 1', title: 'Security Fundamentals', topics: ['CIA Triad & Security Principles', 'Network Security Basics', 'Cryptography Essentials', 'Security Frameworks (NIST, ISO)'] },
        { week: 'Week 2', title: 'Threat Detection & Analysis', topics: ['Log Analysis & SIEM', 'Intrusion Detection Systems (IDS)', 'Malware Analysis Basics', 'Security Monitoring'] },
        { week: 'Week 3', title: 'Vulnerability Assessment', topics: ['Network Scanning (Nmap)', 'Vulnerability Scanners', 'Web Application Security', 'Penetration Testing Basics'] },
        { week: 'Week 4', title: 'Incident Response', topics: ['Incident Response Lifecycle', 'Forensics Basics', 'Threat Intelligence', 'Security Automation'] },
        { week: 'Week 5', title: 'Cloud Security (AWS)', topics: ['AWS Security Basics', 'IAM & Access Control', 'Security Groups & NACLs', 'AWS Security Tools'] },
        { week: 'Week 6', title: 'Cloud Security (Azure)', topics: ['Azure Security Center', 'Azure Sentinel', 'Azure Security Best Practices', 'Cloud Compliance'] },
        { week: 'Week 7', title: 'Security Operations & SOC', topics: ['SOC Operations', 'Security Tools Integration', 'Alert Management', 'Report Writing'] },
        { week: 'Week 8', title: 'Capstone Project', topics: ['Real-world Security Project', 'Security Assessment', 'Portfolio Building', 'Career Preparation'] }
      ],
      skills: ['Network Security', 'Threat Detection', 'Vulnerability Assessment', 'Incident Response', 'SIEM Tools', 'Cloud Security (AWS/Azure)', 'Security Automation', 'Risk Management'],
      tools: ['Wireshark', 'Nmap', 'Metasploit', 'Splunk', 'Burp Suite', 'Kali Linux', 'AWS Security Tools', 'Azure Sentinel', 'Python for Security'],
      careerPaths: ['Cybersecurity Analyst', 'Security Operations Center (SOC) Analyst', 'Incident Response Analyst', 'Security Engineer', 'Cloud Security Specialist'],
      salaryRange: '$70,000 - $110,000',
      prerequisites: ['Basic IT networking knowledge', 'Linux command line basics', 'Understanding of operating systems', 'Completed IT Foundations or equivalent experience']
    },

    'it-foundations': { pdfUrl: 'https://customer-assets.emergentagent.com/job_techpath-preview/artifacts/49hkm8np_Kick-Start%20Your%20IT%20Career%20%E2%80%93%20The%208-Week%20Foundations%20Program.pdf',
      title: 'IT Foundations (8 Weeks)',
      subtitle: 'Kick-Start Your IT Career – The 8-Week Foundations Program',
      duration: '8 Weeks',
      level: 'Beginner',
      format: '100% Online • Live Mentor Support',
      price: 'Contact for Pricing',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80',
      overview: 'Get the fundamentals you need to break into IT: computer and OS basics, networking, cloud fundamentals, basic scripting, tooling, and professional skills. Designed from the PDF guide to help you move quickly from zero to ready.',
      modules: [
        { week: 'Week 1', title: 'Computer & OS Basics', topics: ['Hardware & Peripherals', 'Windows/macOS Basics', 'Files, Processes & Task Manager', 'System Settings'] },
        { week: 'Week 2', title: 'Networking Fundamentals', topics: ['TCP/IP & Subnets', 'DNS, DHCP & NAT', 'Ping/Traceroute', 'Home/Lab Networking'] },
        { week: 'Week 3', title: 'Linux & Command Line', topics: ['Linux Intro', 'Bash Commands', 'File Permissions', 'Package Managers'] },
        { week: 'Week 4', title: 'Cloud Fundamentals', topics: ['What is Cloud?', 'AWS/Azure Accounts', 'Storage & Compute Basics', 'Cloud Security Basics'] },
        { week: 'Week 5', title: 'Scripting & Automation', topics: ['PowerShell Basics', 'Bash Scripting', 'Common Automation Tasks', 'Using Git & GitHub'] },
        { week: 'Week 6', title: 'Productivity & Tooling', topics: ['VS Code', 'Browsers & Extensions', 'Note-taking & Knowledge Base', 'Ticketing Systems'] },
        { week: 'Week 7', title: 'Support & Troubleshooting', topics: ['Helpdesk Workflows', 'Problem Diagnosis', 'Remote Tools', 'Documentation Best Practices'] },
        { week: 'Week 8', title: 'Career Prep & Portfolio', topics: ['Resume Basics', 'LinkedIn Setup', 'Portfolio Checklist', 'Next Steps into Specializations'] }
      ],
      skills: ['Computer & OS Basics','Networking Fundamentals','Linux CLI','Cloud Basics (AWS/Azure)','Scripting (PowerShell/Bash)','Git & GitHub','Troubleshooting','Documentation'],
      tools: ['Windows/macOS', 'Linux', 'PowerShell', 'Bash', 'Git/GitHub', 'VS Code', 'Microsoft 365', 'Ticketing Tools'],
      careerPaths: ['IT Support', 'Service Desk', 'Junior SysAdmin', 'Pathways to Cloud/DevOps/Data'],
      salaryRange: '$45,000 - $65,000',
      prerequisites: ['No prior IT experience required','Basic computer literacy','Willingness to learn and practice']
    },

    'it-support': {
      title: 'IT Support Analyst Program',
      subtitle: 'Launch Your IT Career with Technical Support Excellence',
      duration: '4-5 Months',
      level: 'Beginner',
      format: 'Hybrid (Online + In-Person Labs)',
      price: 'Contact for Pricing',
      image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&q=80',
      overview: 'Start your IT career as an IT Support Analyst. Master troubleshooting, user support, system administration, and technical problem-solving to become the go-to tech support professional.',
      modules: [
        { week: 'Weeks 1-2', title: 'IT Fundamentals', topics: ['Computer Hardware', 'Operating Systems', 'Networking Basics', 'IT Terminology'] },
        { week: 'Weeks 3-5', title: 'Windows Administration', topics: ['Windows 10/11', 'Active Directory', 'Group Policy', 'User Management'] },
        { week: 'Weeks 6-8', title: 'Technical Troubleshooting', topics: ['Problem Diagnosis', 'Ticket Management', 'Remote Support Tools', 'Documentation'] },
        { week: 'Weeks 9-11', title: 'Networking & Security', topics: ['TCP/IP', 'DNS & DHCP', 'VPN', 'Basic Security'] },
        { week: 'Weeks 12-14', title: 'Cloud & Modern Tools', topics: ['Microsoft 365', 'Cloud Services', 'Mobile Device Support', 'Collaboration Tools'] },
        { week: 'Weeks 15-16', title: 'Professional Skills & Project', topics: ['Customer Service', 'Communication Skills', 'Real Support Scenarios', 'Interview Prep'] }
      ],
      skills: ['Windows & macOS Support','Hardware Troubleshooting','Networking Fundamentals','Active Directory','Microsoft 365','Ticketing Systems','Remote Support Tools','Customer Service'],
      tools: ['Windows', 'Active Directory', 'Microsoft 365', 'ServiceNow', 'TeamViewer', 'Zendesk', 'Linux Basics'],
      careerPaths: ['IT Support Analyst','Help Desk Technician','Desktop Support Engineer','IT Service Desk Analyst','Technical Support Specialist','System Administrator'],
      salaryRange: '$50,000 - $70,000',
      prerequisites: ['Basic computer literacy','Good communication skills','Problem-solving mindset','No prior IT experience required']
    },

    'cybersecurity-specialist': {
      title: 'Cybersecurity Specialist Program',
      subtitle: 'Protect Organizations from Cyber Threats',
      duration: '6-8 Weeks',
      level: 'Intermediate to Advanced',
      format: 'Hybrid (Online + In-Person Labs)',
      price: 'Contact for Pricing',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80',
      overview: 'Become a Cybersecurity Analyst equipped to defend organizations against cyber threats. Learn ethical hacking, threat detection, incident response, and security best practices.',
      modules: [
        { week: 'Weeks 1-3', title: 'Security Fundamentals', topics: ['Cybersecurity Basics', 'Network Security', 'Cryptography', 'Security Frameworks'] },
        { week: 'Weeks 4-7', title: 'Threat Detection & Analysis', topics: ['SIEM Tools', 'Log Analysis', 'Threat Intelligence', 'Vulnerability Assessment'] },
        { week: 'Weeks 8-11', title: 'Ethical Hacking', topics: ['Penetration Testing', 'Web Application Security', 'Network Attacks', 'Security Tools'] },
        { week: 'Weeks 12-15', title: 'Incident Response', topics: ['Incident Handling', 'Forensics Basics', 'Malware Analysis', 'Recovery Procedures'] },
        { week: 'Weeks 16-19', title: 'Security Operations', topics: ['SOC Operations', 'Security Monitoring', 'Compliance', 'Risk Management'] },
        { week: 'Weeks 20-24', title: 'Advanced Topics & Project', topics: ['Cloud Security', 'Zero Trust', 'Real-world Security Project', 'Certification Prep'] }
      ],
      skills: ['Network Security','Ethical Hacking','SIEM Tools','Incident Response','Threat Analysis','Vulnerability Assessment','Security Frameworks','Risk Management'],
      tools: ['Splunk', 'Wireshark', 'Metasploit', 'Nmap', 'Burp Suite', 'Kali Linux', 'Security Onion', 'SIEM'],
      careerPaths: ['Cybersecurity Analyst','Security Operations Center (SOC) Analyst','Penetration Tester','Incident Response Analyst','Security Engineer','Threat Intelligence Analyst'],
      salaryRange: '$70,000 - $110,000',
      prerequisites: ['Basic networking knowledge','Understanding of operating systems','Completed IT degree or equivalent','Analytical mindset']
    }
  };

  const program = programsData[programId];

  if (!program) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Program Not Found</h1>
          <Button onClick={() => navigate('/programs')}>View All Programs</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const hasPdf = Boolean(program.pdfUrl);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FreeResourcesBar />

      {/* Hero Section (Indigo theme like AI courses) */}
      <section className="pt-24 pb-16 bg-indigo-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-amber-500 text-slate-900 px-6 py-2 rounded-full font-bold mb-6">
                {program.duration} • {program.level}
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 leading-tight">{program.title}</h1>
              <p className="text-xl text-indigo-100 font-light mb-8">{program.subtitle}</p>
              <div className="grid grid-cols-2 gap-4 mb-8 text-indigo-100">
                <div className="flex items-center gap-2"><Clock className="h-5 w-5" /><span>{program.duration}</span></div>
                <div className="flex items-center gap-2"><Award className="h-5 w-5" /><span>{program.level}</span></div>
                <div className="flex items-center gap-2"><Calendar className="h-5 w-5" /><span>{program.format}</span></div>
                <div className="flex items-center gap-2"><Star className="h-5 w-5" /><span>Salary: {program.salaryRange}</span></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button className="cta-primary" onClick={() => navigate('/contact')}>Enroll Now</button>
                {hasPdf && (
                  <a href={program.pdfUrl} download className="cta-secondary flex items-center justify-center gap-2">
                    <Download className="w-4 h-4" /> Download Course PDF
                  </a>
                )}
              </div>
            </div>
            <div className="relative">
              <img src={program.image} alt={program.title} className="rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Program Overview</h2>
          <p className="text-lg text-slate-600 leading-relaxed">{program.overview}</p>
        </div>
      </section>

      {/* Learning Modules (indigo accents) */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12">Learning Modules</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {program.modules.map((module, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Book className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <div className="text-sm text-indigo-600 font-semibold">{module.week}</div>
                    <h3 className="text-lg font-bold text-slate-900">{module.title}</h3>
                  </div>
                </div>
                <ul className="space-y-2">
                  {module.topics.map((topic, idx) => (
                    <li key={idx} className="flex items-center text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-indigo-600 mr-2 flex-shrink-0" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills & Tools */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Code className="h-6 w-6 text-indigo-600" />
                <h2 className="text-3xl font-bold text-slate-900">Skills You'll Master</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {program.skills.map((skill, index) => (
                  <span key={index} className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium">{skill}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Target className="h-6 w-6 text-indigo-600" />
                <h2 className="text-3xl font-bold text-slate-900">Tools & Technologies</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {program.tools.map((tool, index) => (
                  <span key={index} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">{tool}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career Paths */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Briefcase className="h-6 w-6 text-indigo-600" />
            <h2 className="text-3xl font-bold text-slate-900">Career Opportunities</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {program.careerPaths.map((career, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                <h3 className="font-bold text-slate-900">{career}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prerequisites */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Users className="h-6 w-6 text-indigo-600" />
            <h2 className="text-3xl font-bold text-slate-900">Prerequisites</h2>
          </div>
          <ul className="grid md:grid-cols-2 gap-4">
            {program.prerequisites.map((prereq, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{prereq}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Download full course (IT Foundations) */}
      {hasPdf && (
        <section className="py-16 bg-indigo-700 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Download the Full Course Guide</h2>
            <p className="text-xl text-indigo-100 font-light mb-8">Get the complete program plan with modules, outcomes, and next steps.</p>
            <a href={program.pdfUrl} download className="bg-white text-indigo-700 hover:bg-gray-100 font-semibold px-10 py-6 text-lg rounded-md inline-flex items-center gap-2">
              <Download className="w-5 h-5" /> Download PDF
            </a>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Start Your Journey?</h2>
          <p className="text-xl text-indigo-100 font-light mb-8">Join hundreds of students who have transformed their careers with CareerLift</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-indigo-700 hover:bg-gray-100 font-semibold px-10 py-6 text-lg rounded-md" onClick={() => navigate('/contact')}>Schedule Free Consultation</Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-indigo-700 font-semibold px-10 py-6 text-lg rounded-md" onClick={() => navigate('/programs')}>View All Programs</Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProgramDetailPage;
